import React, { useEffect, useState } from 'react';
import { Box, Card, Divider, Grid, Typography, CircularProgress, Alert, useTheme, useMediaQuery, Select, MenuItem, FormControl, InputLabel, Accordion, AccordionSummary, AccordionDetails, Chip, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart, BarLabel } from '@mui/x-charts/BarChart';
import type { BarLabelProps } from '@mui/x-charts/BarChart';
import { useTranslation } from 'react-i18next';
import { adminApiClient } from '../../adminApiClient';
import { GrowthSummary } from '../../models/Growth';
import { format, parseISO, startOfMonth, subMonths } from 'date-fns';
import type { BookedSpaceStats, BookedTutorStats, MobileAppStats, MonthlyGmv, MonthlyGmvTotal, NewUserActivationStats, ReviewedStudioInfo } from '../../models/Growth';
import GrowthAnalyticsPanels from './GrowthAnalyticsPanels';
import GrowthRetentionPanels from './GrowthRetentionPanels';

const GMV_CURRENCY_COLORS = ['#0d9488', '#6366f1', '#f59e0b', '#ec4899'];

/** Room and tutor series colors, paired per currency (room first). */
const GMV_SPLIT_COLORS = [
  { room: '#0d9488', tutor: '#6366f1' },
  { room: '#f59e0b', tutor: '#ec4899' },
  { room: '#0284c7', tutor: '#a855f7' },
  { room: '#65a30d', tutor: '#e11d48' },
];

function gmvNumber(value: number | null | undefined): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function gmvTotalHasSplit(total: MonthlyGmvTotal | undefined): boolean {
  return total?.roomBookingAmount != null || total?.tutorBookingAmount != null;
}

function formatGmvAmount(amount: number, currencyCode: string, language?: string): string {
  try {
    return new Intl.NumberFormat(language || 'en', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currencyCode} ${Math.round(amount)}`;
  }
}

function formatGmvAmountCompact(amount: number, currencyCode: string, language?: string): string {
  try {
    return new Intl.NumberFormat(language || 'en', {
      style: 'currency',
      currency: currencyCode,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(amount);
  } catch {
    return `${currencyCode} ${Math.round(amount)}`;
  }
}

function gmvTotalsForMonth(rows: MonthlyGmv[] | undefined, monthKey: string): MonthlyGmvTotal[] {
  return rows?.find((row) => row.month === monthKey)?.totals ?? [];
}

function currencyFromGmvSeriesId(seriesId: string | number | undefined, fallback: string): string {
  const currency = String(seriesId ?? '').split(':')[0];
  return currency || fallback;
}

function GmvSplitAmount({
  label,
  amount,
  delta,
  currencyCode,
  color,
  language,
}: {
  label: string;
  amount: number;
  delta: number | null;
  currencyCode: string;
  color: string;
  language?: string;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
        <Typography variant="caption" color="text.secondary" noWrap>
          {label}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {formatGmvAmount(amount, currencyCode, language)}
        </Typography>
        {delta != null && (
          <Typography
            variant="caption"
            sx={{
              color: delta > 0 ? 'success.main' : delta < 0 ? 'error.main' : 'text.secondary',
              fontWeight: 600,
            }}
          >
            {formatSignedCount(Math.round(delta))}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/** Place bar value labels just above each bar (MUI default is centered). */
function AboveBarLabel(props: BarLabelProps) {
  const theme = useTheme();
  const style = (props as { style?: Record<string, any> }).style;

  const heightValue = style?.height;
  const barHeight =
    typeof heightValue === 'number'
      ? heightValue
      : typeof heightValue?.get === 'function'
        ? heightValue.get()
        : 0;

  const yValue = style?.y;
  let nextY = yValue;
  if (typeof yValue?.to === 'function') {
    nextY = yValue.to((value: number) => value - barHeight / 2 - 12);
  } else if (typeof yValue === 'number') {
    nextY = yValue - barHeight / 2 - 12;
  }

  return (
    <BarLabel
      {...props}
      style={{
        ...style,
        y: nextY,
        // SVG text does not always inherit app CSS font; match theme typography.
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.caption.fontSize,
        fontWeight: theme.typography.fontWeightMedium ?? 500,
        fill: theme.palette.text.primary,
      } as React.CSSProperties}
    />
  );
}

function sumSeries(values: number[] | undefined): number {
  if (!values?.length) return 0;
  return values.reduce((sum, v) => sum + (Number(v) || 0), 0);
}

/** Leave headroom so labels above the tallest bars are not clipped. */
function barAxisMax(values: number[] | undefined): number {
  const max = Math.max(0, ...(values ?? [0]));
  if (max <= 0) return 1;
  return max * 1.28;
}

function formatReviewedStudioChipLabel(entry: string | ReviewedStudioInfo): string {
  if (typeof entry === 'string') return entry.trim() || '—';
  const name = entry.studioName?.trim() || entry.spaceId || '—';
  const n = entry.reviewsLeftCount;
  if (n != null && n > 0) {
    return `${name} (${n})`;
  }
  return name;
}

function reviewedStudioRowKey(entry: string | ReviewedStudioInfo, rowKey: string, index: number): string {
  if (typeof entry === 'object' && entry?.spaceId) return `${rowKey}-${entry.spaceId}`;
  return `${rowKey}-studio-${index}`;
}

function formatBookedSpaceChipLabel(entry: BookedSpaceStats): string {
  const name = entry.spaceName?.trim() || entry.spaceId || '—';
  const n = entry.confirmedBookingsCount;
  if (n != null && n > 0) {
    return `${name} (${n})`;
  }
  return name;
}

function bookedSpaceRowKey(entry: BookedSpaceStats, rowKey: string, index: number): string {
  if (entry?.spaceId) return `${rowKey}-booked-${entry.spaceId}`;
  return `${rowKey}-booked-${index}`;
}

/** Normalize API `month` (YYYY-MM or YYYY-MM-DD) to month start for sorting/filtering. */
function parseMonthToStartDate(monthStr: string | undefined | null): Date | null {
  if (monthStr == null || typeof monthStr !== 'string') return null;
  const s = monthStr.trim();
  if (s === '') return null;
  try {
    if (/^\d{4}-\d{2}$/.test(s)) return startOfMonth(parseISO(`${s}-01T12:00:00`));
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      return startOfMonth(parseISO(s.includes('T') ? s : `${s}T12:00:00`));
    }
    const d = parseISO(s);
    if (!Number.isNaN(d.getTime())) return startOfMonth(d);
  } catch {
    /* ignore */
  }
  return null;
}

function formatHostResponseMinutes(m: number | undefined | null): string {
  if (m === undefined || m === null || Number.isNaN(m)) return '—';
  if (m < 60) return `${Math.round(m)} min`;
  const h = Math.floor(m / 60);
  const min = Math.round(m % 60);
  return min > 0 ? `${h}h ${min}m` : `${h}h`;
}

function bookedTutorRowKey(entry: BookedTutorStats, rowKey: string, index: number): string {
  if (entry?.tutorId) return `${rowKey}-tutor-${entry.tutorId}`;
  return `${rowKey}-tutor-${index}`;
}

function formatBookedTutorChipLabel(entry: BookedTutorStats): string {
  const name = entry.tutorName?.trim() || entry.tutorId || '—';
  const n = entry.confirmedBookingsCount;
  if (n != null && n > 0) {
    return `${name} (${n})`;
  }
  return name;
}

function toMonthlyTrend(raw: { day?: string; date?: string; month?: string; count: number }[] | undefined): { dates: Date[]; counts: number[] } {
  if (!raw?.length) return { dates: [], counts: [] };
  const sorted = [...raw]
    .map((c) => ({
      day: c.day || c.date || c.month || '',
      count: c.count,
    }))
    .filter((c) => c.day)
    .sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());
  const dates = sorted.map((c) => {
    const day = c.day.includes('T') ? c.day : (c.day.length === 7 ? `${c.day}-01T00:00:00` : `${c.day}T00:00:00`);
    return parseISO(day);
  });
  const counts = sorted.map((c) => c.count);
  return { dates, counts };
}

/** Format chart deltas for column labels / period totals (e.g. +30, 0, -2). */
function formatSignedCount(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '0';
  if (value > 0) return `+${value}`;
  return String(value);
}

/** Avoids throwing when the API returns missing or non-ISO date strings. */
function formatIsoDateSafe(iso: string | number | undefined | null, fmt: string): string {
  if (iso == null || iso === '') return '—';
  try {
    const d =
      typeof iso === 'number'
        ? new Date(iso)
        : parseISO(String(iso).trim());
    if (Number.isNaN(d.getTime())) return '—';
    return format(d, fmt);
  } catch {
    return '—';
  }
}

type TimePeriod = 'all' | '180d' | '90d' | '30d' | '14d' | '7d' | 'monthly';
type ViewMode = 'daily' | 'monthly';
type SortBy = 'name' | 'bookings';
type SectionTab = 'overall' | 'analytics' | 'retention' | 'bySpace' | 'userStats' | 'tutors' | 'tutorStats';

const GrowthPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [growthData, setGrowthData] = useState<GrowthSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('monthly');
  const [sortBy, setSortBy] = useState<SortBy>('bookings');
  const [expandedSpaces, setExpandedSpaces] = useState<Set<string>>(new Set());
  const [expandedTutors, setExpandedTutors] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<SectionTab>('overall');
  const [bookingDatesDialog, setBookingDatesDialog] = useState<{
    userName: string;
    phoneNumber?: string | null;
    dates: string[];
  } | null>(null);

  useEffect(() => {
    const fetchGrowthData = async () => {
      try {
        setLoading(true);
        const data = await adminApiClient.getGrowth();
        setGrowthData(data);
      } catch (err) {
        setError(t('growth.fetchError', 'Failed to fetch growth data'));
        console.error('Error fetching growth data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGrowthData();
  }, [t]);

  // Helper function to get cutoff date string (YYYY-MM-DD) based on time period
  const getCutoffDateString = (period: TimePeriod): string | null => {
    if (period === 'all') {
      return null;
    }
    
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();
    
    // Create a date at midnight in local timezone
    const todayMidnight = new Date(year, month, day, 0, 0, 0, 0);
    
    let cutoff: Date;
    switch (period) {
      case '7d':
        // Last 7 days: today + 6 previous days = 7 days total
        cutoff = new Date(todayMidnight);
        cutoff.setDate(cutoff.getDate() - 6);
        break;
      case '14d':
        // Last 14 days: today + 13 previous days = 14 days total
        cutoff = new Date(todayMidnight);
        cutoff.setDate(cutoff.getDate() - 13);
        break;
      case '30d':
        // Last 30 days: today + 29 previous days = 30 days total
        cutoff = new Date(todayMidnight);
        cutoff.setDate(cutoff.getDate() - 29);
        break;
      case 'monthly':
        // Current month: from the first day of current month
        cutoff = startOfMonth(todayMidnight);
        break;
      case '90d':
        // Last 90 days: today + 89 previous days = 90 days total
        cutoff = new Date(todayMidnight);
        cutoff.setDate(cutoff.getDate() - 89);
        break;
      case '180d':
        // Last 180 days: today + 179 previous days = 180 days total
        cutoff = new Date(todayMidnight);
        cutoff.setDate(cutoff.getDate() - 179);
        break;
      default:
        return null;
    }
    
    // Return as YYYY-MM-DD string
    const cutoffYear = cutoff.getFullYear();
    const cutoffMonth = String(cutoff.getMonth() + 1).padStart(2, '0');
    const cutoffDay = String(cutoff.getDate()).padStart(2, '0');
    return `${cutoffYear}-${cutoffMonth}-${cutoffDay}`;
  };

  // Helper function to extract YYYY-MM-DD from a date string
  const extractDateString = (dateStr: string): string => {
    // If it's already YYYY-MM-DD format, return it
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    // If it's ISO format with time, extract just the date part
    if (dateStr.includes('T')) {
      return dateStr.split('T')[0];
    }
    // Try to parse and format
    try {
      const date = parseISO(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return dateStr;
    }
  };

  // Helper function to aggregate daily counters into monthly counters
  const aggregateByMonth = (counters: { day?: string; date?: string; count: number }[]): { day: string; count: number }[] => {
    if (!counters || counters.length === 0) {
      return [];
    }
    
    const monthlyMap = new Map<string, number>();
    let earliestDate: Date | null = null;
    let latestDate: Date | null = null;
    let validDatesFound = 0;
    
    // Aggregate counts by month and track earliest/latest dates
    // Handle both 'day' and 'date' field names for compatibility
    counters.forEach((counter, index) => {
      try {
        const dateField = (counter as any).day || (counter as any).date;
        if (!dateField) {
          console.warn(`aggregateByMonth: Counter at index ${index} missing date field:`, counter);
          return;
        }
        
        const dateStr = extractDateString(dateField);
        let date: Date;
        if (dateStr.includes('T')) {
          date = parseISO(dateStr);
        } else {
          date = parseISO(dateStr + 'T00:00:00');
        }
        
        if (isNaN(date.getTime())) {
          console.warn(`aggregateByMonth: Invalid date parsed at index ${index}:`, dateStr);
          return;
        }
        
        const monthStart = startOfMonth(date);
        const monthKey = format(monthStart, 'yyyy-MM-dd');
        monthlyMap.set(monthKey, (monthlyMap.get(monthKey) || 0) + counter.count);
        validDatesFound++;
        
        // Track earliest and latest dates
        if (!earliestDate || monthStart < earliestDate) {
          earliestDate = monthStart;
        }
        if (!latestDate || monthStart > latestDate) {
          latestDate = monthStart;
        }
      } catch (error) {
        console.warn(`aggregateByMonth: Error parsing date at index ${index}:`, counter, error);
      }
    });
    
    // If no valid dates were found, return empty array
    if (validDatesFound === 0 || !earliestDate || !latestDate) {
      return [];
    }
    
    // Fill in all months between earliest and latest, even if they have 0 counts
    const result: { day: string; count: number }[] = [];
    const startDate = new Date(earliestDate);
    const endDate = new Date(latestDate);
    let currentDate = new Date(startDate);
    const endTime = endDate.getTime();
    
    while (currentDate.getTime() <= endTime) {
      const monthKey = format(startOfMonth(currentDate), 'yyyy-MM-dd');
      result.push({
        day: monthKey,
        count: monthlyMap.get(monthKey) || 0
      });
      // Move to next month
      currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    }
    
    return result;
  };

  // Helper function to format data for charts with cumulative counts
  const formatChartData = (counters: { day?: string; date?: string; count: number }[], period: TimePeriod, mode: ViewMode = 'daily') => {
    if (!counters || counters.length === 0) {
      return { dates: [], data: [], deltas: [], baseline: 0 };
    }
    
    // Aggregate by month if monthly view is selected
    let processedCounters = mode === 'monthly' ? aggregateByMonth(counters) : counters.map(c => ({
      day: (c as any).day || (c as any).date || '',
      count: c.count
    }));
    
    // If aggregation returned empty and we're in monthly mode, return empty result
    if (mode === 'monthly' && processedCounters.length === 0) {
      return { dates: [], data: [], deltas: [], baseline: 0 };
    }
    
    const cutoffDateString = getCutoffDateString(period);
    
    // For monthly view, normalize cutoff to start of month for proper comparison
    let cutoffForComparison = cutoffDateString;
    if (mode === 'monthly' && cutoffDateString) {
      try {
        const cutoffDate = parseISO(cutoffDateString + 'T00:00:00');
        cutoffForComparison = format(startOfMonth(cutoffDate), 'yyyy-MM-dd');
      } catch (error) {
        console.warn('Error normalizing cutoff date for monthly view:', error);
      }
    }
    
    // Calculate baseline (cumulative count before the cutoff date)
    let baseline = 0;
    if (cutoffForComparison) {
      const beforeCutoff = processedCounters.filter(counter => {
        const counterDateStr = extractDateString(counter.day);
        return counterDateStr < cutoffForComparison;
      });
      baseline = beforeCutoff.reduce((sum, counter) => sum + counter.count, 0);
    }
    
    // Filter by time period if needed
    let filtered = [...processedCounters];
    if (cutoffForComparison) {
      filtered = processedCounters.filter(counter => {
        const counterDateStr = extractDateString(counter.day);
        // Include dates from cutoff date onward (inclusive)
        return counterDateStr >= cutoffForComparison;
      });
    }
    
    // Sort by date to ensure chronological order
    const sorted = filtered.sort((a, b) => 
      new Date(a.day).getTime() - new Date(b.day).getTime()
    );
    
    // Convert to date objects for time scale
    let dates = sorted.map(counter => {
      try {
        if (counter.day.includes('T')) {
          return parseISO(counter.day);
        } else {
          return new Date(counter.day + 'T00:00:00');
        }
      } catch {
        return new Date(counter.day);
      }
    });
    
    // Store deltas (daily changes)
    const deltas = sorted.map(counter => counter.count);
    
    // Calculate cumulative counts starting from baseline
    let cumulative = baseline;
    const data = sorted.map(counter => {
      cumulative += counter.count;
      return cumulative;
    });
    
    // If there's no data in the period but we have a baseline, add a point at the start
    if (dates.length === 0 && baseline > 0 && cutoffDateString) {
      const [year, month, day] = cutoffDateString.split('-').map(Number);
      const startDate = new Date(year, month - 1, day);
      dates = [startDate];
      data.push(baseline);
      deltas.push(0);
    }
    
    return { dates, data, deltas, baseline };
  };

  // Helper function to create a series with delta tooltip formatter
  const createSeriesWithDeltaFormatter = (
    data: number[], 
    label: string, 
    deltas: number[],
    baseline: number,
    dates: Date[],
    color?: string
  ) => {
    return [{
      data,
      label,
      color: color || theme.palette.primary.main,
      valueFormatter: (value: number | null, context?: { dataIndex?: number }) => {
        if (context?.dataIndex !== undefined && deltas[context.dataIndex] !== undefined) {
          const delta = deltas[context.dataIndex];
          return delta > 0 ? `+${delta}` : `${delta}`;
        }
        return String(value ?? '');
      }
    }];
  };

  // Helper function to render chart based on view mode
  const renderChart = (
    chartData: { dates: Date[]; data: number[]; deltas: number[]; baseline: number },
    label: string,
    color: string
  ) => {
    const overallTotal =
      chartData.data.length > 0
        ? chartData.data[chartData.data.length - 1]
        : chartData.baseline;
    const seriesLabel = `${label} (${overallTotal})`;

    if (viewMode === 'monthly') {
      // For BarChart, use band scale with formatted date strings and show deltas (monthly amounts)
      if (chartData.dates.length === 0) {
        return (
          <Box sx={{ height: isMobile ? 260 : 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">
              {t('growth.noData', 'No data available')}
            </Typography>
          </Box>
        );
      }

      const xAxisLabels = chartData.dates.map((date) => format(date, 'MMM yyyy'));

      const xAxisConfig = {
        scaleType: 'band' as const,
        data: xAxisLabels,
      };

      // For bar charts, use deltas (monthly changes) instead of cumulative values
      const barSeries = [{
        data: chartData.deltas,
        label: seriesLabel,
        color,
        valueFormatter: (value: number | null) => {
          if (value === null || value === undefined) return '0';
          return formatSignedCount(value);
        }
      }];

      const leftMargin = isMobile ? 40 : 56;
      const rightMargin = isMobile ? 12 : 24;

      return (
        <Box sx={{ 
          width: '100%', 
          minWidth: 0, 
          overflowX: 'auto',
          '& .MuiChartsAxis-tickLabel': {
            fontSize: theme.typography.caption.fontSize,
            fontFamily: theme.typography.fontFamily,
          },
          '& .MuiChartsAxis-line': { stroke: theme.palette.divider },
          '& .MuiChartsLegend-label': {
            fontFamily: theme.typography.fontFamily,
          },
        }}>
          <BarChart
            xAxis={[xAxisConfig]}
            yAxis={[{ min: 0, max: barAxisMax(chartData.deltas) }]}
            series={barSeries}
            barLabel={(item) => {
              if (item.value == null || item.value === 0) return null;
              return formatSignedCount(item.value);
            }}
            slots={{ barLabel: AboveBarLabel }}
            skipAnimation
            height={isMobile ? 280 : 340}
            margin={{ top: 28, right: rightMargin, bottom: 48, left: leftMargin }}
            sx={{
              '& .MuiBarElement-root': {
                rx: 6,
                ry: 6,
              },
            }}
          />
        </Box>
      );
    }

    // Daily view: still show monthly-style bars when possible is handled above.
    // For LineChart, use cumulative data with total in the legend.
    const series = createSeriesWithDeltaFormatter(
      chartData.data,
      seriesLabel,
      chartData.deltas,
      chartData.baseline,
      chartData.dates,
      color
    );

    // For LineChart, use time scale
    const xAxisConfig = {
      scaleType: 'time' as const,
      data: chartData.dates,
      valueFormatter: (value: Date | string) => {
        if (value instanceof Date) {
          return format(value, 'MMM d');
        }
        return String(value);
      }
    };

    return (
      <Box sx={{ 
        width: '100%', 
        minWidth: 0, 
        overflowX: 'auto',
        '& .MuiChartsAxis-tickLabel': { fontSize: '0.75rem' },
        '& .MuiChartsAxis-line': { stroke: theme.palette.divider },
        '& .MuiLineElement-root': { strokeWidth: 2.5 },
      }}>
        <LineChart
          xAxis={[xAxisConfig]}
          series={series}
          height={isMobile ? 260 : 320}
          margin={{ top: 24, right: isMobile ? 12 : 24, bottom: 48, left: isMobile ? 40 : 56 }}
        />
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 1200, width: '100%', mx: 'auto' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!growthData) {
    return null;
  }

  const activation: NewUserActivationStats | undefined = growthData.newUserActivationStats;
  const mobileApp: MobileAppStats | undefined = growthData.mobileAppStats;
  const monthlyReviewsTrend = toMonthlyTrend(growthData.monthlyReviewsLeft);
  const monthlyTutorReviewsTrend = toMonthlyTrend(growthData.monthlyTutorReviewsLeft);

  const activeUsersByMonthTrend = (() => {
    const raw = growthData.activeUsersByMonth;
    if (!raw?.length) return { dates: [] as Date[], counts: [] as number[] };
    const cutoffDateString = getCutoffDateString(timePeriod);
    const cutoffMonthStart = cutoffDateString
      ? startOfMonth(parseISO(`${cutoffDateString}T00:00:00`))
      : null;
    const filtered = raw.filter((row) => {
      const d = parseMonthToStartDate(row.month);
      if (!d) return false;
      return !cutoffMonthStart || d.getTime() >= cutoffMonthStart.getTime();
    });
    return toMonthlyTrend(filtered);
  })();

  const activeUsersThisMonth = (() => {
    const currentMonthKey = format(new Date(), 'yyyy-MM');
    return growthData.activeUsersByMonth?.find((row) => row.month === currentMonthKey)?.count ?? 0;
  })();

  const monthlyGmvChart = (() => {
    const raw = growthData.monthlyGmv;
    if (!raw?.length) {
      return {
        labels: [] as string[],
        currencies: [] as string[],
        hasSplit: false,
        valuesByCurrency: [] as number[][],
        roomValuesByCurrency: [] as number[][],
        tutorValuesByCurrency: [] as number[][],
        thisMonthTotals: [] as MonthlyGmvTotal[],
        previousMonthTotals: [] as MonthlyGmvTotal[],
      };
    }
    const cutoffDateString = getCutoffDateString(timePeriod);
    const cutoffMonthStart = cutoffDateString
      ? startOfMonth(parseISO(`${cutoffDateString}T00:00:00`))
      : null;

    const normalized = raw
      .filter((row): row is MonthlyGmv => row != null && typeof row === 'object')
      .map((row) => {
        const d = parseMonthToStartDate(row.month);
        if (!d) return null;
        return {
          d,
          totals: (row.totals ?? []).filter((total) => total?.currencyCode),
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null)
      .filter((x) => !cutoffMonthStart || x.d.getTime() >= cutoffMonthStart.getTime())
      .sort((a, b) => a.d.getTime() - b.d.getTime());

    const currencies = Array.from(
      new Set(normalized.flatMap((row) => row.totals.map((total) => total.currencyCode)))
    ).sort();

    const totalsForCurrency = (currencyCode: string) =>
      normalized.map((row) => row.totals.find((total) => total.currencyCode === currencyCode));

    const hasSplit = normalized.some((row) => row.totals.some((total) => gmvTotalHasSplit(total)));

    return {
      labels: normalized.map((row) => format(row.d, 'MMM yyyy')),
      currencies,
      hasSplit,
      valuesByCurrency: currencies.map((currencyCode) =>
        totalsForCurrency(currencyCode).map((total) => gmvNumber(total?.totalAmount))
      ),
      roomValuesByCurrency: currencies.map((currencyCode) =>
        totalsForCurrency(currencyCode).map((total) => gmvNumber(total?.roomBookingAmount))
      ),
      tutorValuesByCurrency: currencies.map((currencyCode) =>
        totalsForCurrency(currencyCode).map((total) => gmvNumber(total?.tutorBookingAmount))
      ),
      thisMonthTotals: gmvTotalsForMonth(growthData.monthlyGmv, format(new Date(), 'yyyy-MM')),
      previousMonthTotals: gmvTotalsForMonth(growthData.monthlyGmv, format(subMonths(new Date(), 1), 'yyyy-MM')),
    };
  })();

  const monthlyCancellationChart = (() => {
    const raw = growthData.monthlyCancellationBreakdown;
    if (!raw?.length) {
      return { labels: [] as string[], byUser: [] as number[], byHost: [] as number[], byNoResponse: [] as number[] };
    }
    const cutoffDateString = getCutoffDateString(timePeriod);
    const cutoffMonthStart = cutoffDateString
      ? startOfMonth(parseISO(`${cutoffDateString}T00:00:00`))
      : null;

    const normalized = raw
      .filter((row): row is NonNullable<typeof row> => row != null && typeof row === 'object')
      .map((row) => {
        const d = parseMonthToStartDate(
          (row as { month?: string | null }).month
        );
        if (!d) return null;
        return {
          d,
          byUser: (row as { bookingsCancelledByUser?: number }).bookingsCancelledByUser ?? 0,
          byHost: (row as { bookingsCancelledByHost?: number }).bookingsCancelledByHost ?? 0,
          byNoResponse: (row as { bookingsCancelledDueToNoResponse?: number }).bookingsCancelledDueToNoResponse ?? 0,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null)
      .filter((x) => !cutoffMonthStart || x.d.getTime() >= cutoffMonthStart.getTime())
      .sort((a, b) => a.d.getTime() - b.d.getTime());

    return {
      labels: normalized.map((x) => format(x.d, 'MMM yyyy')),
      byUser: normalized.map((x) => x.byUser),
      byHost: normalized.map((x) => x.byHost),
      byNoResponse: normalized.map((x) => x.byNoResponse),
    };
  })();

  const usersChartData = formatChartData(growthData.usersCreated, timePeriod, viewMode);
  const organizationsChartData = formatChartData(growthData.organizationsCreated, timePeriod, viewMode);
  const bookingRequestsChartData = formatChartData(growthData.bookingRequestsCreated, timePeriod, viewMode);
  const bookingsConfirmedChartData = formatChartData(growthData.bookingsConfirmedByHost, timePeriod, viewMode);
  const bookingsCancelledByUserChartData = formatChartData(growthData.bookingsCancelledByUser ?? [], timePeriod, viewMode);
  const bookingsCancelledByHostChartData = formatChartData(growthData.bookingsCancelledByHost ?? [], timePeriod, viewMode);
  const tutorsChartData = formatChartData(growthData.tutorsCreated ?? [], timePeriod, viewMode);
  const tutorBookingRequestsChartData = formatChartData(growthData.tutorBookingRequestsCreated ?? [], timePeriod, viewMode);
  const tutorBookingsConfirmedChartData = formatChartData(growthData.tutorBookingsConfirmed ?? [], timePeriod, viewMode);
  const tutorBookingsCancelledChartData = formatChartData(growthData.tutorBookingsCancelled ?? [], timePeriod, viewMode);
  const tutorBookingsCreatedByTutorChartData = formatChartData(growthData.tutorBookingsCreatedByTutor ?? [], timePeriod, viewMode);
  const appInstallsChartData = formatChartData(mobileApp?.appInstallsByDay ?? [], timePeriod, viewMode);

  // Chart color palette for consistent, polished look
  const chartColors = {
    users: '#6366f1',
    organizations: theme.palette.success.main,
    bookingRequests: '#06b6d4',
    bookingsConfirmed: '#10b981',
    bookingCancelUser: theme.palette.error.main,
    bookingCancelHost: theme.palette.secondary.main,
    tutors: '#8b5cf6',
    tutorBookingRequests: '#14b8a6',
    tutorBookingsConfirmed: '#22c55e',
    tutorBookingsCancelled: '#e11d48',
    tutorCreatedByTutor: '#a855f7',
    mobileApp: '#0ea5e9',
    activeUsers: '#f59e0b',
  };

  /** Align N daily/monthly series by date key; uses deltas (period amounts) for grouped bars. */
  const mergeMultiSeriesForComparison = (series: { dates: Date[]; deltas: number[] }[]) => {
    const dateKeys = new Set<string>();
    const maps = series.map(() => new Map<string, number>());
    const safeDateKey = (d: Date): string | null => {
      if (!d || Number.isNaN(d.getTime())) return null;
      try {
        return format(d, 'yyyy-MM-dd');
      } catch {
        return null;
      }
    };
    series.forEach((s, idx) => {
      s.dates.forEach((d, i) => {
        const key = safeDateKey(d);
        if (!key) return;
        dateKeys.add(key);
        maps[idx].set(key, s.deltas[i] ?? 0);
      });
    });
    const sortedKeys = Array.from(dateKeys).sort();
    return {
      labels: sortedKeys.map((k) => {
        const dt = parseISO(k + 'T00:00:00');
        try {
          return format(dt, viewMode === 'monthly' ? 'MMM yyyy' : 'MMM d');
        } catch {
          return k;
        }
      }),
      valuesBySeries: maps.map((m) => sortedKeys.map((k) => m.get(k) ?? 0)),
    };
  };

  /** Parallel daily/monthly series: requests, confirmed, cancellations by user, cancellations by host. */
  const bookingFlowComparison =
    activeTab === 'overall'
      ? mergeMultiSeriesForComparison([
          bookingRequestsChartData,
          bookingsConfirmedChartData,
          bookingsCancelledByUserChartData,
          bookingsCancelledByHostChartData,
        ])
      : { labels: [] as string[], valuesBySeries: [] as number[][] };

  const tutorFlowComparison =
    activeTab === 'tutors' || activeTab === 'overall'
      ? mergeMultiSeriesForComparison([
          tutorBookingRequestsChartData,
          tutorBookingsConfirmedChartData,
          tutorBookingsCancelledChartData,
          tutorBookingsCreatedByTutorChartData,
        ])
      : { labels: [] as string[], valuesBySeries: [] as number[][] };

  const tutorBookingGrowthCharts = (
    <>
      <Grid item xs={12}>
        <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
            {t('growth.tutorBookingRequests', 'Tutor Booking Requests')}
          </Typography>
          <Typography variant="caption" component="p" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
            {t('growth.tutorBookingRequestsCaption', 'Lesson bookings created (pending, confirmed, and cancelled).')}
          </Typography>
          {tutorBookingRequestsChartData.dates.length > 0 || tutorBookingRequestsChartData.baseline > 0 ? (
            renderChart(tutorBookingRequestsChartData, t('growth.tutorBookingRequests', 'Tutor Booking Requests'), chartColors.tutorBookingRequests)
          ) : (
            <Box sx={{ height: { xs: 260, sm: 300 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">{t('growth.noData', 'No data available')}</Typography>
            </Box>
          )}
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
            {t('growth.tutorBookingsConfirmed', 'Confirmed Tutor Booking requests')}
          </Typography>
          {tutorBookingsConfirmedChartData.dates.length > 0 || tutorBookingsConfirmedChartData.baseline > 0 ? (
            renderChart(tutorBookingsConfirmedChartData, t('growth.tutorBookingsConfirmed', 'Confirmed Tutor Booking requests'), chartColors.tutorBookingsConfirmed)
          ) : (
            <Box sx={{ height: { xs: 260, sm: 300 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">{t('growth.noData', 'No data available')}</Typography>
            </Box>
          )}
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
            {t('growth.tutorBookingFlowTitle', 'Tutor Activity')}
          </Typography>
          {tutorFlowComparison.labels.length > 0 ? (
            <Box sx={{
              width: '100%', minWidth: 0, overflowX: 'auto',
              '& .MuiChartsAxis-tickLabel': { fontSize: theme.typography.caption.fontSize, fontFamily: theme.typography.fontFamily },
              '& .MuiChartsAxis-line': { stroke: theme.palette.divider },
              '& .MuiBarElement-root': { rx: 6, ry: 6 },
              '& .MuiChartsLegend-label': { fontFamily: theme.typography.fontFamily },
            }}>
              <BarChart
                xAxis={[{ scaleType: 'band' as const, data: tutorFlowComparison.labels }]}
                yAxis={[{
                  min: 0,
                  max: barAxisMax(
                    tutorFlowComparison.labels.map((_, i) =>
                      Math.max(
                        tutorFlowComparison.valuesBySeries[0]?.[i] ?? 0,
                        tutorFlowComparison.valuesBySeries[1]?.[i] ?? 0,
                        tutorFlowComparison.valuesBySeries[2]?.[i] ?? 0,
                        tutorFlowComparison.valuesBySeries[3]?.[i] ?? 0,
                      )
                    )
                  ),
                }]}
                series={[
                  {
                    data: tutorFlowComparison.valuesBySeries[0] ?? [],
                    label: `${t('growth.tutorBookingRequests', 'Tutor booking requests')} (${sumSeries(tutorFlowComparison.valuesBySeries[0])})`,
                    color: chartColors.tutorBookingRequests,
                  },
                  {
                    data: tutorFlowComparison.valuesBySeries[1] ?? [],
                    label: `${t('growth.tutorBookingsConfirmed', 'Tutor bookings confirmed')} (${sumSeries(tutorFlowComparison.valuesBySeries[1])})`,
                    color: chartColors.tutorBookingsConfirmed,
                  },
                  {
                    data: tutorFlowComparison.valuesBySeries[2] ?? [],
                    label: `${t('growth.tutorBookingsCancelled', 'Tutor bookings cancelled')} (${sumSeries(tutorFlowComparison.valuesBySeries[2])})`,
                    color: chartColors.tutorBookingsCancelled,
                  },
                  {
                    data: tutorFlowComparison.valuesBySeries[3] ?? [],
                    label: `${t('growth.tutorBookingsCreatedByTutor', 'Lessons created by tutor')} (${sumSeries(tutorFlowComparison.valuesBySeries[3])})`,
                    color: chartColors.tutorCreatedByTutor,
                  },
                ]}
                barLabel={(item) => {
                  if (item.value == null || item.value === 0) return null;
                  return formatSignedCount(item.value);
                }}
                slots={{ barLabel: AboveBarLabel }}
                skipAnimation
                height={isMobile ? 300 : 360}
                margin={{ top: 28, right: isMobile ? 12 : 24, bottom: 56, left: isMobile ? 40 : 56 }}
              />
            </Box>
          ) : (
            <Box sx={{ height: { xs: 260, sm: 300 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">{t('growth.noData', 'No data available')}</Typography>
            </Box>
          )}
        </Card>
      </Grid>
    </>
  );

  const growthTabSx = (value: SectionTab) => ({
    textTransform: 'none' as const,
    fontSize: { xs: '0.875rem' as const, sm: '1.1rem' },
    fontWeight: 600,
    minHeight: { xs: 48, sm: 56 },
    px: { xs: 2, sm: 3 },
    minWidth: { xs: 'auto', sm: 'auto' },
    color: activeTab === value ? theme.palette.primary.main : theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
    transition: 'all 0.2s ease-in-out',
  });

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 4 }, pb: { xs: 8, md: 10 }, maxWidth: 1200, width: '100%', mx: 'auto', minWidth: 0 }}>
      <Card elevation={3} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, overflow: 'visible' }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }, 
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' }, 
          mb: 2, 
          gap: 2 
        }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              {t('growth.title', 'Growth')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('growth.subtitle', 'Track users, organizations, room bookings, and tutor lessons over time.')}
            </Typography>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            flexDirection: { xs: 'column', sm: 'row' },
            minWidth: { xs: '100%', sm: 'auto' }
          }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel>{t('growth.viewMode', 'View Mode')}</InputLabel>
              <Select
                value={viewMode}
                label={t('growth.viewMode', 'View Mode')}
                onChange={(e) => setViewMode(e.target.value as ViewMode)}
              >
                <MenuItem value="daily">{t('growth.daily', 'Daily')}</MenuItem>
                <MenuItem value="monthly">{t('growth.monthly', 'Monthly')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel>{t('growth.timePeriod', 'Time Period')}</InputLabel>
              <Select
                value={timePeriod}
                label={t('growth.timePeriod', 'Time Period')}
                onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
              >
                <MenuItem value="all">{t('growth.allTime', 'All Time')}</MenuItem>
                <MenuItem value="180d">{t('growth.last180d', 'Last 180 Days')}</MenuItem>
                <MenuItem value="90d">{t('growth.last90d', 'Last 90 Days')}</MenuItem>
                <MenuItem value="30d">{t('growth.last30d', 'Last 30 Days')}</MenuItem>
                <MenuItem value="14d">{t('growth.last14d', 'Last 14 Days')}</MenuItem>
                <MenuItem value="monthly">{t('growth.thisMonth', 'This Month')}</MenuItem>
                <MenuItem value="7d">{t('growth.last7d', 'Last 7 Days')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        <Divider />

        {/* Section Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, overflowX: 'auto', mx: { xs: -1.5, sm: -2, md: -3 }, px: { xs: 1.5, sm: 2, md: 3 } }}>
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            aria-label="growth sections"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: { xs: 48, sm: 56 },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                backgroundColor: theme.palette.primary.main,
              },
              '& .MuiTabs-flexContainer': {
                gap: { xs: 0, sm: 0 },
              },
            }}
          >
            <Tab 
              label={t('growth.overall', 'Overall')} 
              value="overall"
              sx={growthTabSx('overall')}
            />
            <Tab
              label={t('growth.analytics', 'Analytics')}
              value="analytics"
              sx={growthTabSx('analytics')}
            />
            <Tab
              label={t('growth.retention.tab', 'Retention')}
              value="retention"
              sx={growthTabSx('retention')}
            />
            <Tab 
              label={t('growth.roomBookings', 'Room Bookings per Space')} 
              value="bySpace"
              sx={growthTabSx('bySpace')}
            />
            <Tab 
              label={t('growth.userRoomBookings', 'User Room Bookings')} 
              value="userStats"
              sx={growthTabSx('userStats')}
            />
            <Tab 
              label={t('growth.tutorBookings', 'Lesson Bookings per Tutor')} 
              value="tutors"
              sx={growthTabSx('tutors')}
            />
            <Tab 
              label={t('growth.userTutorBookings', 'User Tutor Bookings')} 
              value="tutorStats"
              sx={growthTabSx('tutorStats')}
            />
          </Tabs>
        </Box>

        {/* Overall Section */}
        {activeTab === 'overall' && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {/* Header Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h5" gutterBottom>
                    {t('growth.overall', 'Overall')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('growth.overallDescription', 'Users (all accounts), active users, organizations (hosts and tutors), tutors, and platform GMV')}
                  </Typography>
                </Box>

                {/* Summary Statistics */}
                {(() => {
                  // Calculate total users (last cumulative value or baseline)
                  const totalUsers = usersChartData.data.length > 0 
                    ? usersChartData.data[usersChartData.data.length - 1] 
                    : usersChartData.baseline;
                  const totalOrganizations = organizationsChartData.data.length > 0
                    ? organizationsChartData.data[organizationsChartData.data.length - 1]
                    : organizationsChartData.baseline;
                  const totalTutors = tutorsChartData.data.length > 0
                    ? tutorsChartData.data[tutorsChartData.data.length - 1]
                    : tutorsChartData.baseline;
                  
                  return (
                    <>
                      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
                        <Grid item xs={12} sm={6} md={3}>
                          <Card sx={{ p: 2, height: '100%', bgcolor: theme.palette.primary.main, color: 'white' }}>
                            <Typography variant="h4" fontWeight="bold">
                              {totalUsers}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                              {t('growth.totalUsers', 'Users (all)')}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                              {t('growth.totalUsersHint', 'All registered accounts')}
                            </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card sx={{ p: 2, height: '100%', bgcolor: chartColors.activeUsers, color: 'white' }}>
                            <Typography variant="h4" fontWeight="bold">
                              {activeUsersThisMonth}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                              {t('growth.activeUsers', 'Active users')}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                              {t(
                                'growth.activeUsersHint',
                                'Users with a confirmed room or tutor booking this calendar month'
                              )}
                            </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card sx={{ p: 2, height: '100%', bgcolor: chartColors.organizations, color: 'white' }}>
                            <Typography variant="h4" fontWeight="bold">
                              {totalOrganizations}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                              {t('growth.totalOrganizations', 'Organizations')}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                              {t('growth.totalOrganizationsHint', 'Hosts (space and school owners) and tutors, including individual tutors as one-person organizations')}
                            </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card sx={{ p: 2, height: '100%', bgcolor: chartColors.tutors, color: 'white' }}>
                            <Typography variant="h4" fontWeight="bold">
                              {totalTutors}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                              {t('growth.totalTutors', 'Tutors')}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
                              {t('growth.totalTutorsHint', 'Individual tutors and tutors employed at an organization')}
                            </Typography>
                          </Card>
                        </Grid>
                      </Grid>
                    </>
                  );
                })()}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                  {t('growth.gmvByMonth', 'GMV by month')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t(
                    'growth.gmvByMonthDescription',
                    'Confirmed room and tutor booking value across all organizations, by the month the session took place. Each month is split into room bookings and tutor bookings. Test organizations are excluded. The current month includes completed sessions only.'
                  )}
                </Typography>
                {monthlyGmvChart.thisMonthTotals.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                    {monthlyGmvChart.thisMonthTotals.map((total) => {
                      const previousTotal = monthlyGmvChart.previousMonthTotals.find(
                        (row) => row.currencyCode === total.currencyCode
                      );
                      const delta = gmvNumber(total.totalAmount) - gmvNumber(previousTotal?.totalAmount);
                      const showSplit = gmvTotalHasSplit(total);
                      const showPartDelta = !previousTotal || gmvTotalHasSplit(previousTotal);
                      const currencyIndex = Math.max(0, monthlyGmvChart.currencies.indexOf(total.currencyCode));
                      const splitColors = GMV_SPLIT_COLORS[currencyIndex % GMV_SPLIT_COLORS.length];
                      const partDelta = (current: number | null | undefined, previous: number | null | undefined) =>
                        showPartDelta ? gmvNumber(current) - gmvNumber(previous) : null;
                      return (
                        <Box
                          key={total.currencyCode}
                          sx={{
                            px: 1.5,
                            py: 1,
                            borderRadius: 1.5,
                            bgcolor: 'action.hover',
                            minWidth: showSplit ? 240 : 140,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {t('growth.gmvThisMonth', 'GMV this month')} ({total.currencyCode})
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                            {formatGmvAmount(gmvNumber(total.totalAmount), total.currencyCode, i18n.language)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: delta > 0 ? 'success.main' : delta < 0 ? 'error.main' : 'text.secondary',
                              fontWeight: 600,
                            }}
                          >
                            {formatSignedCount(Math.round(delta))} {t('growth.gmvVsPreviousMonth', 'vs previous month')}
                          </Typography>
                          {showSplit && (
                            <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <GmvSplitAmount
                                label={t('growth.gmvRoomBookings', 'Room bookings')}
                                amount={gmvNumber(total.roomBookingAmount)}
                                delta={partDelta(total.roomBookingAmount, previousTotal?.roomBookingAmount)}
                                currencyCode={total.currencyCode}
                                color={splitColors.room}
                                language={i18n.language}
                              />
                              <GmvSplitAmount
                                label={t('growth.gmvTutorBookings', 'Tutor bookings')}
                                amount={gmvNumber(total.tutorBookingAmount)}
                                delta={partDelta(total.tutorBookingAmount, previousTotal?.tutorBookingAmount)}
                                currencyCode={total.currencyCode}
                                color={splitColors.tutor}
                                language={i18n.language}
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
                {monthlyGmvChart.labels.length > 0 ? (
                  <Box sx={{
                    width: '100%',
                    minWidth: 0,
                    overflowX: 'auto',
                    '& .MuiChartsAxis-tickLabel': {
                      fontSize: theme.typography.caption.fontSize,
                      fontFamily: theme.typography.fontFamily,
                    },
                    '& .MuiChartsAxis-line': { stroke: theme.palette.divider },
                    '& .MuiChartsLegend-label': {
                      fontFamily: theme.typography.fontFamily,
                    },
                  }}>
                    <BarChart
                      xAxis={[{
                        scaleType: 'band' as const,
                        data: monthlyGmvChart.labels,
                      }]}
                      yAxis={[{
                        min: 0,
                        max: barAxisMax(
                          monthlyGmvChart.labels.map((_, i) => {
                            if (!monthlyGmvChart.hasSplit) {
                              return Math.max(...monthlyGmvChart.valuesByCurrency.map((series) => series[i] ?? 0), 0);
                            }
                            return Math.max(
                              ...monthlyGmvChart.currencies.map((__, currencyIndex) =>
                                (monthlyGmvChart.roomValuesByCurrency[currencyIndex]?.[i] ?? 0) +
                                (monthlyGmvChart.tutorValuesByCurrency[currencyIndex]?.[i] ?? 0)
                              ),
                              0
                            );
                          })
                        ),
                      }]}
                      series={monthlyGmvChart.hasSplit
                        ? monthlyGmvChart.currencies.flatMap((currencyCode, index) => {
                            const colors = GMV_SPLIT_COLORS[index % GMV_SPLIT_COLORS.length];
                            const formatValue = (value: number | null) =>
                              value == null ? '' : formatGmvAmount(value, currencyCode, i18n.language);
                            return [
                              {
                                id: `${currencyCode}:room`,
                                stack: currencyCode,
                                data: monthlyGmvChart.roomValuesByCurrency[index] ?? [],
                                label: `${t('growth.gmvRoomBookings', 'Room bookings')} (${currencyCode})`,
                                color: colors.room,
                                valueFormatter: formatValue,
                              },
                              {
                                id: `${currencyCode}:tutor`,
                                stack: currencyCode,
                                data: monthlyGmvChart.tutorValuesByCurrency[index] ?? [],
                                label: `${t('growth.gmvTutorBookings', 'Tutor bookings')} (${currencyCode})`,
                                color: colors.tutor,
                                valueFormatter: formatValue,
                              },
                            ];
                          })
                        : monthlyGmvChart.currencies.map((currencyCode, index) => ({
                            id: currencyCode,
                            data: monthlyGmvChart.valuesByCurrency[index] ?? [],
                            label: `${t('growth.gmvSeriesLabel', 'GMV')} (${currencyCode})`,
                            color: GMV_CURRENCY_COLORS[index % GMV_CURRENCY_COLORS.length],
                            valueFormatter: (value: number | null) =>
                              value == null ? '' : formatGmvAmount(value, currencyCode, i18n.language),
                          }))}
                      barLabel={(item) => {
                        if (item.value == null || item.value === 0) return null;
                        const currencyCode = currencyFromGmvSeriesId(
                          item.seriesId,
                          monthlyGmvChart.currencies[0] || 'PLN'
                        );
                        return formatGmvAmountCompact(item.value, currencyCode, i18n.language);
                      }}
                      slots={{ barLabel: AboveBarLabel }}
                      skipAnimation
                      height={isMobile ? 280 : 340}
                      margin={{ top: 28, right: isMobile ? 12 : 24, bottom: 48, left: isMobile ? 48 : 64 }}
                      sx={{
                        '& .MuiBarElement-root': { rx: 6, ry: 6 },
                      }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ height: { xs: 200, sm: 240 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">
                      {t('growth.noData', 'No data available')}
                    </Typography>
                  </Box>
                )}
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                  {t('growth.activeUsersByMonth', 'Active users by month')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t(
                    'growth.activeUsersByMonthDescription',
                    'Distinct users with at least one confirmed room or tutor booking in each calendar month.'
                  )}
                </Typography>
                {activeUsersByMonthTrend.dates.length > 0 ? (
                  <Box sx={{
                    width: '100%',
                    minWidth: 0,
                    overflowX: 'auto',
                    '& .MuiChartsAxis-tickLabel': {
                      fontSize: theme.typography.caption.fontSize,
                      fontFamily: theme.typography.fontFamily,
                    },
                    '& .MuiChartsAxis-line': { stroke: theme.palette.divider },
                    '& .MuiChartsLegend-label': {
                      fontFamily: theme.typography.fontFamily,
                    },
                  }}>
                    <BarChart
                      xAxis={[{
                        scaleType: 'band' as const,
                        data: activeUsersByMonthTrend.dates.map((d) => format(d, 'MMM yyyy')),
                      }]}
                      yAxis={[{ min: 0, max: barAxisMax(activeUsersByMonthTrend.counts) }]}
                      series={[{
                        data: activeUsersByMonthTrend.counts,
                        label: `${t('growth.activeUsers', 'Active users')} (${sumSeries(activeUsersByMonthTrend.counts)})`,
                        color: chartColors.activeUsers,
                        valueFormatter: (value: number | null) =>
                          value == null ? '' : String(value),
                      }]}
                      barLabel={(item) => {
                        if (item.value == null || item.value === 0) return null;
                        return String(item.value);
                      }}
                      slots={{ barLabel: AboveBarLabel }}
                      skipAnimation
                      height={isMobile ? 280 : 340}
                      margin={{ top: 28, right: isMobile ? 12 : 24, bottom: 48, left: isMobile ? 40 : 56 }}
                      sx={{
                        '& .MuiBarElement-root': { rx: 6, ry: 6 },
                      }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ height: { xs: 200, sm: 240 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">
                      {t('growth.noData', 'No data available')}
                    </Typography>
                  </Box>
                )}
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                  {t('growth.userCount', 'User count')}
                </Typography>
            {usersChartData.dates.length > 0 || usersChartData.baseline > 0 ? (
              renderChart(
                usersChartData,
                t('growth.users', 'Users'),
                chartColors.users
              )
            ) : (
              <Box sx={{ height: { xs: 260, sm: 300 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">
                  {t('growth.noData', 'No data available')}
                </Typography>
              </Box>
            )}
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                  {t('growth.organizationCount', 'Organization count')}
                </Typography>
            {organizationsChartData.dates.length > 0 || organizationsChartData.baseline > 0 ? (
              renderChart(
                organizationsChartData,
                t('growth.organizations', 'Organizations'),
                chartColors.organizations
              )
            ) : (
              <Box sx={{ height: { xs: 260, sm: 300 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">
                  {t('growth.noData', 'No data available')}
                </Typography>
              </Box>
            )}
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                  {t('growth.tutorCount', 'Tutor count')}
                </Typography>
                {tutorsChartData.dates.length > 0 || tutorsChartData.baseline > 0 ? (
                  renderChart(tutorsChartData, t('growth.tutors', 'Tutors'), chartColors.tutors)
                ) : (
                  <Box sx={{ height: { xs: 260, sm: 300 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">{t('growth.noData', 'No data available')}</Typography>
                  </Box>
                )}
              </Card>
            </Grid>

            {activation && (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                    {t('growth.newUserActivation', 'New user activation')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t(
                      'growth.newUserActivationDescription',
                      'Cohort metrics for new users in their first {{hours}} hours after signup.',
                      { hours: activation.cohortWindowHours }
                    )}
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                        <Typography variant="h5" fontWeight={700}>
                          {activation.usersBookedInFirst48Hours ?? 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            'growth.usersBookedInCohortWindow',
                            'Users with a booking in the first {{hours}}h',
                            { hours: activation.cohortWindowHours }
                          )}
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                        <Typography variant="h5" fontWeight={700}>
                          {activation.hostResolvedBookingsCount ?? 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('growth.hostResolvedBookings', 'Host-resolved bookings')}
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                        <Typography variant="h5" fontWeight={700}>
                          {formatHostResponseMinutes(activation.averageHostResponseMinutes)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('growth.averageHostResponse', 'Avg. host response time')}
                        </Typography>
                      </Card>
                    </Grid>
                  </Grid>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                    {t('growth.cancellationsByMonth', 'Cancellations by month')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                    {t('growth.cancellationsByMonthHint', 'Stacked by reason; respects the time period filter above.')}
                  </Typography>
                  {monthlyCancellationChart.labels.length > 0 ? (
                    <Box sx={{
                      width: '100%',
                      minWidth: 0,
                      overflowX: 'auto',
                      '& .MuiChartsAxis-tickLabel': { fontSize: '0.75rem' },
                      '& .MuiChartsAxis-line': { stroke: theme.palette.divider },
                      '& .MuiBarElement-root': { rx: 4, ry: 4 },
                    }}>
                      <BarChart
                        xAxis={[{ scaleType: 'band' as const, data: monthlyCancellationChart.labels }]}
                        yAxis={[{
                          min: 0,
                          max: barAxisMax(
                            monthlyCancellationChart.labels.map((_, i) =>
                              (monthlyCancellationChart.byUser[i] ?? 0) +
                              (monthlyCancellationChart.byHost[i] ?? 0) +
                              (monthlyCancellationChart.byNoResponse[i] ?? 0)
                            )
                          ),
                        }]}
                        series={[
                          {
                            data: monthlyCancellationChart.byUser,
                            label: `${t('growth.cancelledByUser', 'Cancelled by user')} (${sumSeries(monthlyCancellationChart.byUser)})`,
                            stack: 'cancellations',
                            color: theme.palette.error.main,
                          },
                          {
                            data: monthlyCancellationChart.byHost,
                            label: `${t('growth.cancelledByHost', 'Cancelled by host')} (${sumSeries(monthlyCancellationChart.byHost)})`,
                            stack: 'cancellations',
                            color: chartColors.tutors,
                          },
                          {
                            data: monthlyCancellationChart.byNoResponse,
                            label: `${t('growth.cancelledNoResponse', 'No response')} (${sumSeries(monthlyCancellationChart.byNoResponse)})`,
                            stack: 'cancellations',
                            color: theme.palette.grey[500],
                          },
                        ]}
                        barLabel={(item) => {
                          if (item.value == null || item.value === 0) return null;
                          return formatSignedCount(item.value);
                        }}
                        slots={{ barLabel: AboveBarLabel }}
                        skipAnimation
                        height={isMobile ? 300 : 360}
                        margin={{ top: 28, right: isMobile ? 12 : 24, bottom: 56, left: isMobile ? 40 : 56 }}
                        sx={{
                          '& .MuiChartsLegend-label, & .MuiChartsAxis-tickLabel': {
                            fontFamily: theme.typography.fontFamily,
                          },
                        }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t('growth.noMonthlyCancellations', 'No monthly cancellation breakdown available for this period.')}
                      </Typography>
                      {(() => {
                        const u = activation.bookingsCancelledByUser ?? 0;
                        const h = activation.bookingsCancelledByHost ?? 0;
                        const n = activation.bookingsCancelledDueToNoResponse ?? 0;
                        if (u + h + n === 0) return null;
                        return (
                          <Typography variant="body2" color="text.secondary">
                            {t(
                              'growth.cohortCancellationTotalsFallback',
                              'Cohort totals (all-time): {{user}} by user, {{host}} by host, {{noResponse}} no response.',
                              { user: u, host: h, noResponse: n }
                            )}
                          </Typography>
                        );
                      })()}
                    </Box>
                  )}
                </Card>
              </Grid>
            )}

            {mobileApp && (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                    {t('growth.mobileApp', 'Mobile app')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t(
                      'growth.mobileAppDescription',
                      'Users with a registered mobile device (push). Counts are users, not device rows.'
                    )}
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} sm={4} md={2}>
                      <Card variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                        <Typography variant="h5" fontWeight={700}>
                          {mobileApp.usersWithAppInstalled ?? 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('growth.usersWithAppInstalled', 'App installed')}
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                      <Card variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                        <Typography variant="h5" fontWeight={700}>
                          {mobileApp.usersWithIos ?? 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('growth.usersWithIos', 'iOS')}
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                      <Card variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                        <Typography variant="h5" fontWeight={700}>
                          {mobileApp.usersWithAndroid ?? 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('growth.usersWithAndroid', 'Android')}
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                      <Card variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                        <Typography variant="h5" fontWeight={700}>
                          {mobileApp.usersWithBothPlatforms ?? 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('growth.usersWithBothPlatforms', 'Both platforms')}
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                      <Card variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                        <Typography variant="h5" fontWeight={700}>
                          {mobileApp.enabledPushUsers ?? 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('growth.enabledPushUsers', 'Push enabled')}
                        </Typography>
                      </Card>
                    </Grid>
                  </Grid>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                    {t('growth.appInstallsOverTime', 'First app installs over time')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                    {t(
                      'growth.appInstallsOverTimeHint',
                      'Distinct users by day of their first device registration. Respects the time period filter above.'
                    )}
                  </Typography>
                  {appInstallsChartData.dates.length > 0 || appInstallsChartData.baseline > 0 ? (
                    renderChart(
                      appInstallsChartData,
                      t('growth.appInstalls', 'App installs'),
                      chartColors.mobileApp
                    )
                  ) : (
                    <Box sx={{ height: { xs: 200, sm: 240 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography color="text.secondary">
                        {t('growth.noData', 'No data available')}
                      </Typography>
                    </Box>
                  )}
                </Card>
              </Grid>
            )}

            {monthlyReviewsTrend.dates.length > 0 && (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                    {t('growth.monthlyReviews', 'Monthly reviews')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('growth.monthlyReviewsDescription', 'Reviews left per month (non-cumulative).')}
                  </Typography>
                  <Box sx={{
                    width: '100%',
                    minWidth: 0,
                    overflowX: 'auto',
                    '& .MuiChartsAxis-tickLabel': {
                      fontSize: theme.typography.caption.fontSize,
                      fontFamily: theme.typography.fontFamily,
                    },
                    '& .MuiChartsAxis-line': { stroke: theme.palette.divider },
                    '& .MuiChartsLegend-label': {
                      fontFamily: theme.typography.fontFamily,
                    },
                  }}>
                    <BarChart
                      xAxis={[{
                        scaleType: 'band' as const,
                        data: monthlyReviewsTrend.dates.map((d) => format(d, 'MMM yyyy')),
                      }]}
                      yAxis={[{ min: 0, max: barAxisMax(monthlyReviewsTrend.counts) }]}
                      series={[{
                        data: monthlyReviewsTrend.counts,
                        label: `${t('growth.reviewsPerMonth', 'Reviews')} (${sumSeries(monthlyReviewsTrend.counts)})`,
                        color: '#ec4899',
                        valueFormatter: (value: number | null) =>
                          value == null ? '' : formatSignedCount(value),
                      }]}
                      barLabel={(item) => {
                        if (item.value == null || item.value === 0) return null;
                        return formatSignedCount(item.value);
                      }}
                      slots={{ barLabel: AboveBarLabel }}
                      skipAnimation
                      height={isMobile ? 280 : 340}
                      margin={{ top: 28, right: isMobile ? 12 : 24, bottom: 48, left: isMobile ? 40 : 56 }}
                      sx={{
                        '& .MuiBarElement-root': { rx: 6, ry: 6 },
                      }}
                    />
                  </Box>
                </Card>
              </Grid>
            )}

          <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                  {t('growth.roomBookingRequestsTitle', 'Room Booking Requests')}
                </Typography>
                <Typography variant="caption" component="p" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                  {t('growth.userBookingRequestsCaption', 'Booking requests initiated by users (distinct from host-created bookings).')}
                </Typography>
                {bookingRequestsChartData.dates.length > 0 || bookingRequestsChartData.baseline > 0 ? (
                  renderChart(bookingRequestsChartData, t('growth.roomBookingRequestsTitle', 'Room Booking Requests'), chartColors.bookingRequests)
                ) : (
                  <Box sx={{ height: { xs: 260, sm: 300 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">{t('growth.noData', 'No data available')}</Typography>
                  </Box>
                )}
              </Card>
            </Grid>
          <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                  {t('growth.roomBookingsConfirmedTitle', 'Confirmed room booking requests')}
                </Typography>
                {bookingsConfirmedChartData.dates.length > 0 || bookingsConfirmedChartData.baseline > 0 ? (
                  renderChart(bookingsConfirmedChartData, t('growth.roomBookingsConfirmedTitle', 'Confirmed room booking requests'), chartColors.bookingsConfirmed)
                ) : (
                  <Box sx={{ height: { xs: 260, sm: 300 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">{t('growth.noData', 'No data available')}</Typography>
                  </Box>
                )}
              </Card>
            </Grid>

            {/* Parallel daily/monthly series: requests, confirmations, cancellations */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', mt: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                  {t('growth.bookingFlowComparisonTitle', 'Room activity')}
                </Typography>
                <Typography variant="caption" component="p" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                  {t('growth.bookingFlowComparisonCaption', 'Room requests, confirmations, and cancellations. Period amounts (Δ).')}
                </Typography>
              {bookingFlowComparison.labels.length > 0 ? (
                <Box sx={{ 
                  width: '100%', minWidth: 0, overflowX: 'auto',
                  '& .MuiChartsAxis-tickLabel': {
                    fontSize: theme.typography.caption.fontSize,
                    fontFamily: theme.typography.fontFamily,
                  },
                  '& .MuiChartsAxis-line': { stroke: theme.palette.divider },
                  '& .MuiBarElement-root': { rx: 6, ry: 6 },
                  '& .MuiChartsLegend-label': {
                    fontFamily: theme.typography.fontFamily,
                  },
                }}>
                  <BarChart
                    xAxis={[{ scaleType: 'band' as const, data: bookingFlowComparison.labels }]}
                    yAxis={[{
                      min: 0,
                      max: barAxisMax(
                        bookingFlowComparison.labels.map((_, i) =>
                          Math.max(
                            bookingFlowComparison.valuesBySeries[0]?.[i] ?? 0,
                            bookingFlowComparison.valuesBySeries[1]?.[i] ?? 0,
                            bookingFlowComparison.valuesBySeries[2]?.[i] ?? 0,
                            bookingFlowComparison.valuesBySeries[3]?.[i] ?? 0,
                          )
                        )
                      ),
                    }]}
                    series={[
                      {
                        data: bookingFlowComparison.valuesBySeries[0] ?? [],
                        label: `${t('growth.roomRequestsSeries', 'Room requests')} (${sumSeries(bookingFlowComparison.valuesBySeries[0])})`,
                        color: chartColors.bookingRequests,
                      },
                      {
                        data: bookingFlowComparison.valuesBySeries[1] ?? [],
                        label: `${t('growth.roomConfirmedSeries', 'Rooms confirmed')} (${sumSeries(bookingFlowComparison.valuesBySeries[1])})`,
                        color: chartColors.bookingsConfirmed,
                      },
                      {
                        data: bookingFlowComparison.valuesBySeries[2] ?? [],
                        label: `${t('growth.dailyCancellationsByUser', 'Cancellations by user')} (${sumSeries(bookingFlowComparison.valuesBySeries[2])})`,
                        color: chartColors.bookingCancelUser,
                      },
                      {
                        data: bookingFlowComparison.valuesBySeries[3] ?? [],
                        label: `${t('growth.dailyCancellationsByHost', 'Cancellations by host')} (${sumSeries(bookingFlowComparison.valuesBySeries[3])})`,
                        color: chartColors.bookingCancelHost,
                      },
                    ]}
                    barLabel={(item) => {
                      if (item.value == null || item.value === 0) return null;
                      return formatSignedCount(item.value);
                    }}
                    slots={{ barLabel: AboveBarLabel }}
                    skipAnimation
                    height={isMobile ? 300 : 360}
                    margin={{ top: 28, right: isMobile ? 12 : 24, bottom: 56, left: isMobile ? 40 : 56 }}
                  />
                </Box>
              ) : (
                <Box sx={{ height: { xs: 260, sm: 300 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">{t('growth.noData', 'No data available')}</Typography>
                </Box>
              )}
              </Card>
            </Grid>
            {tutorBookingGrowthCharts}
          </Grid>
        )}

        {/* Bookings by Space Section */}
        {activeTab === 'analytics' && <GrowthAnalyticsPanels data={growthData} />}

        {activeTab === 'retention' && <GrowthRetentionPanels data={growthData} />}

        {activeTab === 'bySpace' && growthData.bookingsConfirmedByHostBySpace && Object.keys(growthData.bookingsConfirmedByHostBySpace).filter(spaceName => spaceName !== 'Band Space Test Space').length > 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {/* Header Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' }, 
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'stretch', sm: 'center' }, 
                  mb: 2, 
                  gap: 2 
                }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {t('growth.roomBookings', 'Room Bookings per Space')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('growth.roomBookingsDescription', 'Confirmed room bookings broken down by space')}
                    </Typography>
                  </Box>
                  <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                    <InputLabel>{t('growth.sortBy', 'Sort By')}</InputLabel>
                    <Select
                      value={sortBy}
                      label={t('growth.sortBy', 'Sort By')}
                      onChange={(e) => setSortBy(e.target.value as SortBy)}
                    >
                      <MenuItem value="bookings">{t('growth.sortByBookings', 'Total Bookings')}</MenuItem>
                      <MenuItem value="name">{t('growth.sortByName', 'Space Name')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Summary Statistics */}
                {(() => {
                  const cutoffDateString = getCutoffDateString(timePeriod);
                  const spaceEntries = Object.entries(growthData.bookingsConfirmedByHostBySpace)
                    .filter(([spaceName]) => spaceName !== 'Band Space Test Space')
                    .map(([spaceName, counters]) => {
                    let filteredCounters = counters;
                    if (cutoffDateString) {
                      filteredCounters = counters.filter(counter => {
                        const dateField = (counter as any).day || (counter as any).date;
                        if (!dateField) return false;
                        const counterDateStr = extractDateString(dateField);
                        return counterDateStr >= cutoffDateString;
                      });
                    }
                    const totalBookings = filteredCounters.reduce((sum, counter) => sum + counter.count, 0);
                    
                    // Calculate active period count based on view mode (for consistency, though not displayed here)
                    let activePeriods: number;
                    if (viewMode === 'monthly') {
                      // Count unique months
                      const uniqueMonths = new Set<string>();
                      filteredCounters.forEach(counter => {
                        try {
                          const dateField = (counter as any).day || (counter as any).date;
                          if (!dateField) return;
                          const dateStr = extractDateString(dateField);
                          let date: Date;
                          if (dateStr.includes('T')) {
                            date = parseISO(dateStr);
                          } else {
                            date = parseISO(dateStr + 'T00:00:00');
                          }
                          if (!isNaN(date.getTime())) {
                            const monthKey = format(startOfMonth(date), 'yyyy-MM');
                            uniqueMonths.add(monthKey);
                          }
                        } catch (error) {
                          // Skip invalid dates
                        }
                      });
                      activePeriods = uniqueMonths.size;
                    } else {
                      // Count days (original behavior)
                      activePeriods = filteredCounters.length;
                    }
                    
                    return { spaceName, totalBookings, activeDays: activePeriods };
                  });
                  
                  const totalBookingsAll = spaceEntries.reduce((sum, entry) => sum + entry.totalBookings, 0);
                  const totalSpaces = spaceEntries.length;
                  const avgBookingsPerSpace = totalSpaces > 0 ? Math.round(totalBookingsAll / totalSpaces) : 0;
                  
                  return (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={4}>
                        <Card sx={{ p: 2, bgcolor: theme.palette.primary.main, color: 'white' }}>
                          <Typography variant="h4" fontWeight="bold">
                            {totalBookingsAll}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {t('growth.totalConfirmedBookings', 'Total Confirmed Bookings')}
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Card sx={{ p: 2, bgcolor: theme.palette.secondary.main, color: 'white' }}>
                          <Typography variant="h4" fontWeight="bold">
                            {totalSpaces}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {t('growth.activeSpaces', 'Active Spaces')}
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Card sx={{ p: 2, bgcolor: theme.palette.success.main, color: 'white' }}>
                          <Typography variant="h4" fontWeight="bold">
                            {avgBookingsPerSpace}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {t('growth.avgBookingsPerSpace', 'Avg per Space')}
                          </Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  );
                })()}
              </Box>

              {/* Space List */}
              {(() => {
                const cutoffDateString = getCutoffDateString(timePeriod);
                const spaceEntries = Object.entries(growthData.bookingsConfirmedByHostBySpace)
                  .filter(([spaceName]) => spaceName !== 'Band Space Test Space')
                  .map(([spaceName, counters]) => {
                  let filteredCounters = counters;
                  if (cutoffDateString) {
                    filteredCounters = counters.filter(counter => {
                      const dateField = (counter as any).day || (counter as any).date;
                      if (!dateField) return false;
                      const counterDateStr = extractDateString(dateField);
                      return counterDateStr >= cutoffDateString;
                    });
                  }
                  const totalBookings = filteredCounters.reduce((sum, counter) => sum + counter.count, 0);
                  
                  // Calculate active period count based on view mode
                  let activePeriods: number;
                  if (viewMode === 'monthly') {
                    // Count unique months
                    const uniqueMonths = new Set<string>();
                    filteredCounters.forEach(counter => {
                      try {
                        const dateField = (counter as any).day || (counter as any).date;
                        if (!dateField) return;
                        const dateStr = extractDateString(dateField);
                        let date: Date;
                        if (dateStr.includes('T')) {
                          date = parseISO(dateStr);
                        } else {
                          date = parseISO(dateStr + 'T00:00:00');
                        }
                        if (!isNaN(date.getTime())) {
                          const monthKey = format(startOfMonth(date), 'yyyy-MM');
                          uniqueMonths.add(monthKey);
                        }
                      } catch (error) {
                        // Skip invalid dates
                      }
                    });
                    activePeriods = uniqueMonths.size;
                  } else {
                    // Count days (original behavior)
                    activePeriods = filteredCounters.length;
                  }
                  
                  return { spaceName, counters, totalBookings, activeDays: activePeriods };
                });

                // Sort based on selected option
                const sortedEntries = [...spaceEntries].sort((a, b) => {
                  if (sortBy === 'bookings') {
                    return b.totalBookings - a.totalBookings;
                  }
                  return a.spaceName.localeCompare(b.spaceName);
                });

                return sortedEntries.map(({ spaceName, counters, totalBookings, activeDays }) => {
                  const spaceChartData = formatChartData(counters, timePeriod, viewMode);
                  const isExpanded = expandedSpaces.has(spaceName);
                  
                  return (
                    <Card key={spaceName} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                      <Accordion 
                        expanded={isExpanded}
                        onChange={() => {
                          const newExpanded = new Set(expandedSpaces);
                          if (isExpanded) {
                            newExpanded.delete(spaceName);
                          } else {
                            newExpanded.add(spaceName);
                          }
                          setExpandedSpaces(newExpanded);
                        }}
                        sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
                      >
                        <AccordionSummary 
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ 
                            px: { xs: 1.5, sm: 2 }, 
                            py: 1.5,
                            '&:hover': { bgcolor: 'action.hover' },
                            minHeight: { xs: 48, sm: 56 },
                            '&.Mui-expanded': { minHeight: { xs: 48, sm: 56 } }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: { xs: 1, sm: 2 }, flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flex: 1, flexWrap: 'wrap', minWidth: 0 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {spaceName}
                              </Typography>
                              <Chip 
                                label={`${totalBookings} ${t('growth.bookings', 'bookings')}`}
                                color="primary"
                                size="small"
                                icon={<TrendingUpIcon />}
                              />
                              <Typography variant="body2" color="text.secondary">
                                {activeDays} {viewMode === 'monthly' 
                                  ? t('growth.months', 'months') 
                                  : t('growth.days', 'days')}
                              </Typography>
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 2, pb: 2 }}>
                          <Divider sx={{ mb: 2 }} />
                          {spaceChartData.dates.length > 0 || spaceChartData.baseline > 0 ? (
                            renderChart(
                              spaceChartData,
                              t('growth.bookingsConfirmed', 'Confirmed Bookings'),
                              chartColors.bookingsConfirmed
                            )
                          ) : (
                            <Box sx={{ height: { xs: 260, sm: 300 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography color="text.secondary">
                                {t('growth.noData', 'No data available')}
                              </Typography>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    </Card>
                  );
                });
              })()}
            </Grid>
          </Grid>
        )}

        {/* User Booking Stats Section */}
        {activeTab === 'userStats' && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  {t('growth.userRoomBookings', 'User Room Bookings')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('growth.userRoomBookingsDescription', 'Users with confirmed room bookings and their activity')}
                </Typography>
                {Array.isArray(growthData.usersBookingStats) && growthData.usersBookingStats.length > 0 && (
                  <Card sx={{ p: 2, bgcolor: theme.palette.primary.main, color: 'white', display: 'inline-block', mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">
                      {growthData.usersBookingStats.length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {t('growth.usersWithBookings', 'users have confirmed bookings')}
                    </Typography>
                  </Card>
                )}
              </Box>
              {Array.isArray(growthData.usersBookingStats) && growthData.usersBookingStats.length > 0 ? (
                <TableContainer 
                  component={Paper} 
                  sx={{ 
                    border: '1px solid', 
                    borderColor: 'divider', 
                    borderRadius: 1,
                    overflowX: 'auto',
                    maxWidth: '100%',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Table size="small" sx={{ minWidth: isMobile ? 780 : undefined }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 600 }}>{t('growth.userName', 'User')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{t('growth.confirmedBookingsCount', 'Confirmed Bookings')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('growth.bookedSpaces', 'Booked spaces')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{t('growth.reviewsLeftCount', 'Reviews left')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('growth.reviewedStudios', 'Reviewed studios')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('growth.latestBookingDate', 'Latest Booking')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('growth.bookingDates', 'Booking Dates')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...growthData.usersBookingStats]
                        .sort((a, b) => (Number(b.confirmedBookingsCount) || 0) - (Number(a.confirmedBookingsCount) || 0))
                        .map((stat, rowIndex) => {
                          const rowKey = stat.userId ?? `row-${rowIndex}`;
                          const reviewedStudios = Array.isArray(stat.reviewedStudios) ? stat.reviewedStudios : [];
                          // Preserve backend order (highest count, then latest date); do not derive from reviews.
                          const bookedSpaces = Array.isArray(stat.bookedSpaces) ? stat.bookedSpaces : [];
                          const bookingDates = Array.isArray(stat.confirmedBookingDates) ? stat.confirmedBookingDates : [];
                          return (
                        <TableRow key={rowKey} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {stat.userName || stat.userId || '—'}
                            </Typography>
                            {stat.phoneNumber ? (
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                {stat.phoneNumber}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={String(stat.confirmedBookingsCount ?? 0)} size="small" color="primary" />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 300 }}>
                            {bookedSpaces.length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {bookedSpaces.slice(0, 8).map((entry, i) => (
                                  <Tooltip
                                    key={bookedSpaceRowKey(entry, rowKey, i)}
                                    title={t('growth.bookedSpaceLatest', 'Latest: {{date}}', {
                                      date: formatIsoDateSafe(entry.latestBookingDate, 'MMM d, yyyy HH:mm'),
                                    })}
                                  >
                                    <Chip
                                      label={formatBookedSpaceChipLabel(entry)}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  </Tooltip>
                                ))}
                                {bookedSpaces.length > 8 && (
                                  <Chip label={`+${bookedSpaces.length - 8}`} size="small" sx={{ fontSize: '0.7rem', bgcolor: 'action.hover' }} />
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {stat.reviewsLeftCount != null ? (
                              <Chip label={String(stat.reviewsLeftCount)} size="small" variant="outlined" />
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>
                            {reviewedStudios.length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {reviewedStudios.slice(0, 8).map((entry, i) => (
                                  <Chip
                                    key={reviewedStudioRowKey(entry, rowKey, i)}
                                    label={formatReviewedStudioChipLabel(entry)}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                ))}
                                {reviewedStudios.length > 8 && (
                                  <Chip label={`+${reviewedStudios.length - 8}`} size="small" sx={{ fontSize: '0.7rem', bgcolor: 'action.hover' }} />
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatIsoDateSafe(stat.latestBookingDate, 'MMM d, yyyy HH:mm')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ 
                              display: 'grid', 
                              gridTemplateColumns: { xs: 'repeat(auto-fill, minmax(70px, 1fr))', sm: 'repeat(auto-fill, minmax(80px, 1fr))' }, 
                              gap: 0.5, 
                              maxWidth: { xs: '100%', sm: 400 } 
                            }}>
                              {bookingDates.slice(0, 12).map((dateStr, i) => (
                                <Chip
                                  key={`${rowKey}-bd-${i}`}
                                  label={formatIsoDateSafe(dateStr, 'MMM d HH:mm')}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 24, '& .MuiChip-label': { px: 0.75 } }}
                                />
                              ))}
                              {bookingDates.length > 12 && (
                                <Chip
                                  label={t('growth.showMoreBookingDates', '+{{count}} more', {
                                    count: bookingDates.length - 12,
                                  })}
                                  size="small"
                                  clickable
                                  onClick={() =>
                                    setBookingDatesDialog({
                                      userName: stat.userName || stat.userId || '—',
                                      phoneNumber: stat.phoneNumber,
                                      dates: bookingDates,
                                    })
                                  }
                                  sx={{
                                    fontSize: '0.7rem',
                                    height: 24,
                                    bgcolor: 'action.hover',
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.selected' },
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('growth.noUserStats', 'No user booking stats available')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('growth.noUserStatsDescription', 'There are no users with confirmed bookings to display.')}
                  </Typography>
                </Box>
              )}

              <Dialog
                open={bookingDatesDialog != null}
                onClose={() => setBookingDatesDialog(null)}
                fullWidth
                maxWidth="sm"
              >
                <DialogTitle sx={{ pb: 1 }}>
                  {t('growth.allBookingDatesTitle', 'All booking dates')}
                  {bookingDatesDialog && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
                      {bookingDatesDialog.userName}
                      {bookingDatesDialog.phoneNumber ? ` · ${bookingDatesDialog.phoneNumber}` : ''}
                      {` · ${bookingDatesDialog.dates.length}`}
                    </Typography>
                  )}
                </DialogTitle>
                <DialogContent dividers>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(auto-fill, minmax(96px, 1fr))',
                        sm: 'repeat(auto-fill, minmax(110px, 1fr))',
                      },
                      gap: 0.75,
                    }}
                  >
                    {(bookingDatesDialog?.dates ?? []).map((dateStr, i) => (
                      <Chip
                        key={`dialog-bd-${i}`}
                        label={formatIsoDateSafe(dateStr, 'MMM d, yyyy HH:mm')}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem', justifyContent: 'flex-start' }}
                      />
                    ))}
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setBookingDatesDialog(null)}>
                    {t('growth.close', 'Close')}
                  </Button>
                </DialogActions>
              </Dialog>
            </Grid>
          </Grid>
        )}

        {/* Tutors Section */}
        {activeTab === 'tutors' && growthData.tutorBookingsConfirmedByTutor && Object.keys(growthData.tutorBookingsConfirmedByTutor).length > 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  mb: 2,
                  gap: 2,
                }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {t('growth.tutorBookings', 'Lesson Bookings per Tutor')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('growth.tutorBookingsByTutorDescription', 'Confirmed lessons broken down by tutor')}
                    </Typography>
                  </Box>
                  <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                    <InputLabel>{t('growth.sortBy', 'Sort By')}</InputLabel>
                    <Select
                      value={sortBy}
                      label={t('growth.sortBy', 'Sort By')}
                      onChange={(e) => setSortBy(e.target.value as SortBy)}
                    >
                      <MenuItem value="bookings">{t('growth.sortByBookings', 'Total Bookings')}</MenuItem>
                      <MenuItem value="name">{t('growth.sortByName', 'Tutor Name')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                {(() => {
                  const cutoffDateString = getCutoffDateString(timePeriod);
                  const tutorEntries = Object.entries(growthData.tutorBookingsConfirmedByTutor ?? {}).map(([, counters]) => {
                    let filteredCounters = counters;
                    if (cutoffDateString) {
                      filteredCounters = counters.filter((counter) => {
                        const dateField = (counter as { day?: string; date?: string }).day || (counter as { date?: string }).date;
                        if (!dateField) return false;
                        return extractDateString(dateField) >= cutoffDateString;
                      });
                    }
                    return filteredCounters.reduce((sum, counter) => sum + counter.count, 0);
                  });
                  const totalBookingsAll = tutorEntries.reduce((sum, count) => sum + count, 0);
                  const totalTutorsWithLessons = tutorEntries.length;
                  const avgPerTutor = totalTutorsWithLessons > 0 ? Math.round(totalBookingsAll / totalTutorsWithLessons) : 0;
                  return (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={4}>
                        <Card sx={{ p: 2, bgcolor: theme.palette.primary.main, color: 'white' }}>
                          <Typography variant="h4" fontWeight="bold">{totalBookingsAll}</Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>{t('growth.totalConfirmedTutorBookings', 'Confirmed lessons (period)')}</Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Card sx={{ p: 2, bgcolor: theme.palette.secondary.main, color: 'white' }}>
                          <Typography variant="h4" fontWeight="bold">{totalTutorsWithLessons}</Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>{t('growth.activeTutors', 'Tutors with confirmed lessons')}</Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Card sx={{ p: 2, bgcolor: theme.palette.success.main, color: 'white' }}>
                          <Typography variant="h4" fontWeight="bold">{avgPerTutor}</Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>{t('growth.avgBookingsPerTutor', 'Avg per Tutor')}</Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  );
                })()}
              </Box>
            </Grid>
            <Grid item xs={12}>
              {(() => {
                const byTutor = growthData.tutorBookingsConfirmedByTutor ?? {};
                const cutoffDateString = getCutoffDateString(timePeriod);
                const tutorEntries = Object.entries(byTutor).map(([tutorName, counters]) => {
                  let filteredCounters = counters;
                  if (cutoffDateString) {
                    filteredCounters = counters.filter((counter) => {
                      const dateField = (counter as { day?: string; date?: string }).day || (counter as { date?: string }).date;
                      if (!dateField) return false;
                      return extractDateString(dateField) >= cutoffDateString;
                    });
                  }
                  const totalBookings = filteredCounters.reduce((sum, counter) => sum + counter.count, 0);
                  let activePeriods = filteredCounters.length;
                  if (viewMode === 'monthly') {
                    const uniqueMonths = new Set<string>();
                    filteredCounters.forEach((counter) => {
                      const dateField = (counter as { day?: string; date?: string }).day || (counter as { date?: string }).date;
                      if (!dateField) return;
                      try {
                        const dateStr = extractDateString(dateField);
                        const date = parseISO(dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00`);
                        if (!Number.isNaN(date.getTime())) uniqueMonths.add(format(startOfMonth(date), 'yyyy-MM'));
                      } catch {
                        // skip invalid dates
                      }
                    });
                    activePeriods = uniqueMonths.size;
                  }
                  return { tutorName, counters, totalBookings, activeDays: activePeriods };
                });
                const sortedEntries = [...tutorEntries].sort((a, b) => {
                  if (sortBy === 'bookings') return b.totalBookings - a.totalBookings;
                  return a.tutorName.localeCompare(b.tutorName);
                });
                if (sortedEntries.length === 0) {
                  return (
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        {t('growth.noTutorSpaceData', 'No tutor booking data available')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('growth.noTutorSpaceDataDescription', 'There are no confirmed tutor bookings to display.')}
                      </Typography>
                    </Box>
                  );
                }
                return sortedEntries.map(({ tutorName, counters, totalBookings, activeDays }) => {
                  const tutorChartData = formatChartData(counters, timePeriod, viewMode);
                  const isExpanded = expandedTutors.has(tutorName);
                  return (
                    <Card key={tutorName} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                      <Accordion
                        expanded={isExpanded}
                        onChange={() => {
                          const next = new Set(expandedTutors);
                          if (isExpanded) next.delete(tutorName);
                          else next.add(tutorName);
                          setExpandedTutors(next);
                        }}
                        sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            px: { xs: 1.5, sm: 2 },
                            py: 1.5,
                            '&:hover': { bgcolor: 'action.hover' },
                            minHeight: { xs: 48, sm: 56 },
                            '&.Mui-expanded': { minHeight: { xs: 48, sm: 56 } },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: { xs: 1, sm: 2 }, flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flex: 1, flexWrap: 'wrap', minWidth: 0 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {tutorName}
                              </Typography>
                              <Chip label={`${totalBookings} ${t('growth.bookings', 'bookings')}`} color="primary" size="small" icon={<TrendingUpIcon />} />
                              <Typography variant="body2" color="text.secondary">
                                {activeDays} {viewMode === 'monthly' ? t('growth.months', 'months') : t('growth.days', 'days')}
                              </Typography>
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 2, pb: 2 }}>
                          <Divider sx={{ mb: 2 }} />
                          {tutorChartData.dates.length > 0 || tutorChartData.baseline > 0 ? (
                            renderChart(tutorChartData, t('growth.tutorBookingsConfirmed', 'Tutor bookings confirmed'), chartColors.tutorBookingsConfirmed)
                          ) : (
                            <Box sx={{ height: { xs: 260, sm: 300 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography color="text.secondary">{t('growth.noData', 'No data available')}</Typography>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    </Card>
                  );
                });
              })()}
            </Grid>
          </Grid>
        )}

        {/* User Tutor Bookings */}
        {activeTab === 'tutorStats' && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  {t('growth.userTutorBookings', 'User Tutor Bookings')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('growth.userTutorBookingsDescription', 'Users with confirmed tutor lessons')}
                </Typography>
                {Array.isArray(growthData.usersTutorBookingStats) && growthData.usersTutorBookingStats.length > 0 && (
                  <Card sx={{ p: 2, bgcolor: theme.palette.secondary.main, color: 'white', display: 'inline-block', mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">{growthData.usersTutorBookingStats.length}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>{t('growth.usersWithTutorBookings', 'students have confirmed tutor lessons')}</Typography>
                  </Card>
                )}
              </Box>
              {Array.isArray(growthData.usersTutorBookingStats) && growthData.usersTutorBookingStats.length > 0 ? (
                <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflowX: 'auto', maxWidth: '100%', bgcolor: 'background.paper' }}>
                  <Table size="small" sx={{ minWidth: isMobile ? 720 : undefined }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 600 }}>{t('growth.userName', 'User')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{t('growth.confirmedBookingsCount', 'Confirmed Bookings')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('growth.bookedTutors', 'Booked tutors')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('growth.latestBookingDate', 'Latest Booking')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('growth.bookingDates', 'Booking Dates')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...growthData.usersTutorBookingStats]
                        .sort((a, b) => (Number(b.confirmedBookingsCount) || 0) - (Number(a.confirmedBookingsCount) || 0))
                        .map((stat, rowIndex) => {
                          const rowKey = stat.userId ?? `student-${rowIndex}`;
                          const bookedTutors = Array.isArray(stat.bookedTutors) ? stat.bookedTutors : [];
                          const bookingDates = Array.isArray(stat.confirmedBookingDates) ? stat.confirmedBookingDates : [];
                          return (
                            <TableRow key={rowKey} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>{stat.userName || stat.userId || '—'}</Typography>
                                {stat.phoneNumber ? (
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                    {stat.phoneNumber}
                                  </Typography>
                                ) : null}
                              </TableCell>
                              <TableCell align="right"><Chip label={String(stat.confirmedBookingsCount ?? 0)} size="small" color="primary" /></TableCell>
                              <TableCell sx={{ maxWidth: 300 }}>
                                {bookedTutors.length > 0 ? (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {bookedTutors.slice(0, 8).map((entry, i) => (
                                      <Tooltip
                                        key={bookedTutorRowKey(entry, rowKey, i)}
                                        title={t('growth.bookedTutorLatest', 'Latest: {{date}}', {
                                          date: formatIsoDateSafe(entry.latestBookingDate, 'MMM d, yyyy HH:mm'),
                                        })}
                                      >
                                        <Chip label={formatBookedTutorChipLabel(entry)} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                      </Tooltip>
                                    ))}
                                    {bookedTutors.length > 8 && (
                                      <Chip label={`+${bookedTutors.length - 8}`} size="small" sx={{ fontSize: '0.7rem', bgcolor: 'action.hover' }} />
                                    )}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">—</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {formatIsoDateSafe(stat.latestBookingDate, 'MMM d, yyyy HH:mm')}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(auto-fill, minmax(70px, 1fr))', sm: 'repeat(auto-fill, minmax(80px, 1fr))' }, gap: 0.5, maxWidth: { xs: '100%', sm: 400 } }}>
                                  {bookingDates.slice(0, 12).map((dateStr, i) => (
                                    <Chip key={`${rowKey}-bd-${i}`} label={formatIsoDateSafe(dateStr, 'MMM d HH:mm')} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 24, '& .MuiChip-label': { px: 0.75 } }} />
                                  ))}
                                  {bookingDates.length > 12 && (
                                    <Chip
                                      label={t('growth.showMoreBookingDates', '+{{count}} more', { count: bookingDates.length - 12 })}
                                      size="small"
                                      clickable
                                      onClick={() => setBookingDatesDialog({ userName: stat.userName || stat.userId || '—', phoneNumber: stat.phoneNumber, dates: bookingDates })}
                                      sx={{ fontSize: '0.7rem', height: 24, bgcolor: 'action.hover', cursor: 'pointer', '&:hover': { bgcolor: 'action.selected' } }}
                                    />
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('growth.noUserTutorStats', 'No student tutor-booking stats available')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('growth.noUserTutorStatsDescription', 'There are no students with confirmed tutor lessons to display.')}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        )}

        {/* Show message if no space data available */}
        {activeTab === 'bySpace' && (!growthData.bookingsConfirmedByHostBySpace || Object.keys(growthData.bookingsConfirmedByHostBySpace).filter(spaceName => spaceName !== 'Band Space Test Space').length === 0) && (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('growth.noSpaceData', 'No space booking data available')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('growth.noSpaceDataDescription', 'There are no confirmed bookings by space to display.')}
            </Typography>
          </Box>
        )}
        {activeTab === 'tutors' && (!growthData.tutorBookingsConfirmedByTutor || Object.keys(growthData.tutorBookingsConfirmedByTutor).length === 0) && (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('growth.noTutorSpaceData', 'No tutor booking data available')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('growth.noTutorSpaceDataDescription', 'There are no confirmed tutor bookings to display.')}
            </Typography>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default GrowthPage;
